import React, { useMemo } from 'react';
import clsx from 'clsx';
import Decimal from 'decimal.js';
import { PieChart } from '../PieChart/PieChart';
import { PieChartProps } from '../PieChart/pieChart.helpers';
import { PortfolioTable, PortfolioTableVariant } from '../PortfolioTable/PortfolioTable';
import { usePortfolioHoverSync } from './usePortfolioHoverSync';

import useStyles from './PortfolioStructureChart.style';

export interface PortfolioStructureChartRow {
  name: string;
  share: Decimal;
  value?: Decimal;
  color: string;
  testId?: string;
}

export interface PortfolioStructureChartProps {
  ariaLabel: string;
  rows: PortfolioStructureChartRow[];
  productHeader: React.ReactNode;
  shareHeader: React.ReactNode;
  valueHeader: React.ReactNode;

  pieData?: PieChartProps['data'];
  hideTooltip?: boolean;
  chartWidth?: number;
  chartHeight?: number;

  variant?: PortfolioTableVariant;
  className?: string;
  chartClassName?: string;
  tableClassName?: string;
  enableHoverSync?: boolean;
}

const buildPieDataFromRows = (rows: PortfolioStructureChartRow[]): PieChartProps['data'] => {
  return rows.map(r => ({
    name: r.name,
    color: r.color,
    y: +r.share.toNumber().toFixed(2),
  }));
};

const PortfolioStructureChart: React.FC<PortfolioStructureChartProps> = ({
  ariaLabel,
  rows,
  productHeader,
  shareHeader,
  valueHeader,
  pieData,
  hideTooltip = false,
  chartWidth = 250,
  chartHeight = 250,
  variant = 'longTitle',
  className,
  chartClassName,
  tableClassName,
  enableHoverSync = true,
}) => {
  const c = useStyles({});
  const { pieChartRef, tableRows, onPointHover } = usePortfolioHoverSync({ rows, enableHoverSync });

  const computedPieData = useMemo(() => pieData ?? buildPieDataFromRows(rows), [pieData, rows]);

  return (
    <div className={clsx(c.root, className)}>
      <div className={clsx(c.chartBox, chartClassName)}>
        <PieChart
          ref={pieChartRef}
          data={computedPieData}
          hideLegend={true}
          hideTooltip={hideTooltip}
          chartWidth={chartWidth}
          chartHeight={chartHeight}
          onPointHover={onPointHover}
        />
      </div>
      <div className={c.tableBox}>
        <PortfolioTable
          className={tableClassName}
          ariaLabel={ariaLabel}
          rows={tableRows}
          productHeader={productHeader}
          shareHeader={shareHeader}
          valueHeader={valueHeader}
          variant={variant}
        />
      </div>
    </div>
  );
};

export { PortfolioStructureChart };

